import React from 'react'

import { AvatarStack, Box, Button, Card, Tag, Text } from '@factorialco/deprecated-design-system'
import { AnimatePresence } from 'framer-motion'

import Modal from 'modules/core/components/Modal'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import Compensation, { UNITS_TYPE_UNITS } from 'modules/contracts/store/models/Compensation'
import {
  CompensationTemplateUI,
  EmployeeMetaMap,
} from 'modules/employees/pages/BulkFixContractsPage/types'
import generateAvatars from 'modules/employees/pages/BulkFixContractsPage/utils/generateAvatars'

interface Props {
  employeeIds: number[]
  targetEmployees: EmployeeMetaMap
  compensationTemplate: CompensationTemplateUI
  removalIndex: number
  onRemove: (index: number) => void
}

const CompensationSummaryWidget: React.FC<Props> = ({
  targetEmployees,
  employeeIds,
  compensationTemplate,
  removalIndex,
  onRemove,
}) => {
  const {
    taxonomyName,
    legalEntityName,
    policyName,
    syncWithSupplements,
    calculation,
    compensationType,
    amount,
    unit,
    startsOn,
    firstPaymentOn,
  } = compensationTemplate

  const amountClause = useMemo(() => {
    const isCurrencyAmount = unit !== UNITS_TYPE_UNITS

    if (compensationType === Compensation.types.undefined) {
      const base = i18n.t('compensation.unit.base')

      return i18n.t(
        'employees.bulk_edit.fix_conditions.steps.compensations.summary_card.amount_readout_undefined',
        {
          unit: isCurrencyAmount ? unit : base,
        }
      )
    }

    const amountDisplayed = isCurrencyAmount
      ? `${amount} ${unit}`
      : i18n.tp('compensation.unit.units', {
          count: Number(amount ?? 0),
        })

    switch (compensationType) {
      // undefined already handled
      case 'undefined':
      case 'fixed':
        return i18n.t(
          'employees.bulk_edit.fix_conditions.steps.compensations.summary_card.amount_readout_fixed',
          {
            amount: amountDisplayed,
          }
        )
      case 'per_worked_hour':
        return i18n.t(
          'employees.bulk_edit.fix_conditions.steps.compensations.summary_card.amount_readout_per_worked_hour',
          {
            amount: amountDisplayed,
          }
        )
      case 'per_worked_day':
        const calcDisplayed =
          calculation === Compensation.calculations.current_period
            ? i18n.t(
                'employees.bulk_edit.fix_conditions.steps.compensations.summary_card.current_period'
              )
            : i18n.t(
                'employees.bulk_edit.fix_conditions.steps.compensations.summary_card.previous_period'
              )

        return i18n.t(
          'employees.bulk_edit.fix_conditions.steps.compensations.summary_card.amount_readout_per_worked_day',
          {
            amount: amountDisplayed,
            calculation: calcDisplayed,
          }
        )
      case 'up_to':
        return i18n.t(
          'employees.bulk_edit.fix_conditions.steps.compensations.summary_card.amount_readout_up_to',
          {
            amount: amountDisplayed,
          }
        )
      default:
        const _exhaustiveCheck: never = compensationType
        throw new Error(`Unhandled compensation type: ${_exhaustiveCheck}`)
    }
  }, [unit, amount, compensationType, calculation])

  const startFromDate = startsOn
    ? convertDate(startsOn, 'backend', 'ui')
    : convertDate(firstPaymentOn ?? '', 'backend', 'ui_month_year')

  const amountSummaryLine = i18n.t(
    'employees.bulk_edit.fix_conditions.steps.compensations.summary_card.amount_summary_line',
    {
      amount_readout: amountClause,
      start_date: startFromDate,
    }
  )

  const scopeClause = `${legalEntityName} / ${policyName}`

  const avatars = useUnsafeMemo(
    () => generateAvatars(employeeIds, targetEmployees),
    [employeeIds, targetEmployees]
  )

  const [showConfirmRemoveDialog, setShowConfirmRemoveDialog] = React.useState(false)

  return (
    <>
      <AnimatePresence>
        {showConfirmRemoveDialog && (
          <Modal
            title={i18n.t('employees.bulk_edit.fix_conditions.confirm_remove_modal.title')}
            description={i18n.t(
              'employees.bulk_edit.fix_conditions.confirm_remove_modal.description'
            )}
            onClose={() => setShowConfirmRemoveDialog(false)}
            onSubmit={() => {
              onRemove(removalIndex)
              setShowConfirmRemoveDialog(false)
            }}
            footer={
              <>
                <Button onClick={() => setShowConfirmRemoveDialog(false)} type="secondary">
                  {i18n.t('common.cancel')}
                </Button>
                <Button type="negative" submit>
                  {i18n.t('common.delete')}
                </Button>
              </>
            }
          />
        )}
      </AnimatePresence>
      <Card>
        {syncWithSupplements && (
          <Card.Eyebrow>
            <Tag>
              {i18n.t(
                'employees.bulk_edit.fix_conditions.steps.compensations.summary_card.synced_label'
              )}
            </Tag>
          </Card.Eyebrow>
        )}
        <Card.Title
          action={
            <Button
              iconName="delete"
              onClick={() => {
                setShowConfirmRemoveDialog(true)
              }}
              type="secondary"
            />
          }
        >
          {taxonomyName}
        </Card.Title>
        <Card.Content>
          <Box gap="s16">
            <Box>
              <Text.Normal size="100" weight="semibold">
                {i18n.t('contract_versions.forms.additional_compensation.modal.amount_field')}
              </Text.Normal>
              <Text.Normal>{amountSummaryLine}</Text.Normal>
            </Box>
            <Box>
              <Text.Normal size="100" weight="semibold">
                {i18n.t(
                  'employees.bulk_edit.fix_conditions.steps.compensations.summary_card.scope_label'
                )}
              </Text.Normal>
              <Text.Normal>{scopeClause}</Text.Normal>
            </Box>

            <AvatarStack
              size="s32"
              avatars={avatars}
              leftover={employeeIds.length - avatars.length}
              textSize="700"
              textColor="olive1000"
              backgroundColor="olive1000"
            />
          </Box>
        </Card.Content>
      </Card>
    </>
  )
}

export default CompensationSummaryWidget
